import { useRouter } from "next/router";
import { createContext, ReactNode, useContext } from "react";

import useContentMetadataGet from "@/content/useContentMetadataGet";
import useLangstonContentGet from "@/content/useLangstonContentGet";
import ResponseError from "@/errors/ResponseError";
import { UseRestQueryResponse } from "@/remoteState/useRestQuery/useRestQuery";
import { LangstonContentResponse } from "@/types/models/Content.interface";
import ContentLevel from "@/types/models/ContentLevel.interface";
import { Metadata } from "@/view/ContentHead";
import { isArticle } from "@/view/utils/contentTypeChecks";
import useUpdateLevel from "@/view/utils/useUpdateLevel";

type ViewPageContentContextType = {
  content: LangstonContentResponse | null | undefined;
  contentRequestError: ResponseError | undefined;
  isContentLoading: boolean;
  contentMetadata: Metadata[] | null | undefined;
  contentMetadataRequestError: typeof ResponseError | undefined;
  currentLevel: ContentLevel | undefined;
  contentMetadataLoading: boolean;
  setCurrentLevel: (level: ContentLevel | undefined) => void;
};

const ViewPageContentContext = createContext<
  ViewPageContentContextType | undefined
>(undefined);

export const ViewPageContentContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Router
  const router = useRouter();
  const { query } = router;
  const { contentId } = query as {
    contentId: string;
  };

  // Fetching content/story data
  const {
    data: content,
    error,
    loading: isContentLoading,
  }: UseRestQueryResponse<LangstonContentResponse> = useLangstonContentGet<LangstonContentResponse>(
    contentId,
  );

  // Fetching content metadata
  const {
    data: contentMetadata,
    error: contentMetadataRequestError,
    loading: contentMetadataLoading,
  } = useContentMetadataGet(contentId);

  const [currentLevel, setCurrentLevel] = useUpdateLevel(
    isArticle(content?.data) ? (content?.data?.article_levels ?? []) : [],
  );

  // Ensure error is an instance of ResponseError or undefined
  const contentRequestError: ResponseError | undefined =
    error instanceof ResponseError ? error : undefined;

  return (
    <ViewPageContentContext.Provider
      value={{
        content,
        contentRequestError,
        isContentLoading,
        contentMetadata,
        contentMetadataRequestError,
        contentMetadataLoading,
        currentLevel,
        setCurrentLevel,
      }}
    >
      {children}
    </ViewPageContentContext.Provider>
  );
};

const useViewPageContentContext = () => {
  const viewPageContentContext = useContext(ViewPageContentContext);

  if (viewPageContentContext === undefined) {
    throw new Error(
      "ViewPageContentContext must be used within a ViewPageContentContextProvider.",
    );
  }

  return viewPageContentContext;
};

export default useViewPageContentContext;
